/**
 * ¿Estamos en el host de la aplicación o en el de un estudio?
 *
 * La misma SPA sirve tres cosas distintas según el dominio desde el que se pida:
 *
 *   chasquitambo.com       → la web pública y el alta
 *   acme.chasquitambo.com  → el panel del estudio
 *   entrega.estudio.com    → dominio propio de un estudio (también su panel)
 *
 * La regla es la del backend puesta del revés: el dominio raíz y un puñado de
 * subdominios reservados no son ningún tenant, así que ahí va la web. Cualquier
 * otra cosa —incluido un dominio propio, que no termina en el dominio raíz—
 * pertenece a un estudio.
 *
 * La dirección buena es el APEX, no `app.`. Los dos subdominios de abajo siguen
 * sirviendo la web para que un enlace viejo no muera, pero el canónico es
 * `chasquitambo.com` y ahí es donde se manda a la gente.
 */
const MARKETING_SUBDOMAINS = ['app', 'www']

/** En desarrollo, `lvh.me` resuelve a 127.0.0.1 con todos sus subdominios. */
const ROOT_DOMAIN = import.meta.env.VITE_ROOT_DOMAIN ?? 'lvh.me'

/**
 * El dominio raíz, para ENSEÑARLO.
 *
 * Existe porque estaba escrito a mano en la interfaz —«.chasquitambo.com» literal en
 * el campo de «Entrar» y en las dos ayudas del alta— mientras la navegación
 * usaba esta constante. Es decir: al estudio se le prometía una dirección y se
 * le llevaba a otra. En producción coincidían por casualidad y en desarrollo no
 * coincidían nunca; el día de cambiar de dominio habría dejado de coincidir en
 * los dos sitios a la vez.
 *
 * Con esto, lo que se lee y a dónde se va salen de la misma línea.
 */
export function rootDomain(): string {
  return ROOT_DOMAIN
}

export function isMarketingHost(hostname: string = window.location.hostname): boolean {
  // Cómodo en desarrollo: `localhost` no es ningún tenant, así que entrar por
  // ahí y ver un login que nunca va a funcionar sólo confunde.
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true

  if (hostname === ROOT_DOMAIN) return true

  if (!hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    // Dominio propio de un estudio: no es la web.
    return false
  }

  const label = hostname.slice(0, -(ROOT_DOMAIN.length + 1))

  return MARKETING_SUBDOMAINS.includes(label)
}

/**
 * URL del panel de un estudio a partir de su dirección.
 *
 * Se construye en el cliente porque la web pública no tiene con qué preguntarle
 * al backend: `chasquitambo.com` no resuelve ningún tenant, y comprobar si
 * un slug existe antes de redirigir sería además un oráculo para averiguar qué
 * estudios son clientes.
 */
export function studioUrl(slug: string, path = '/login'): string {
  const { protocol, port } = window.location
  const suffix = port === '' ? '' : `:${port}`

  return `${protocol}//${slug}.${ROOT_DOMAIN}${suffix}${path}`
}
